import { useContext, useMemo } from "react";
import { trpc } from "@/trpc";
import { cn } from "@/lib/utils";
import MerchantContext from "@/contexts/MerchantContext";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import TagChip from "./TagChip";

type LeadTagChipsProps = {
  tagIds: string[] | null | undefined;
  max?: number;
  className?: string;
};

const LeadTagChips = ({ tagIds, max = 3, className }: LeadTagChipsProps) => {
  const { merchantId } = useContext(MerchantContext);
  const { data: catalog } = trpc.leadTags.list.useQuery(
    { merchantId },
    { enabled: !!merchantId, staleTime: 5 * 60 * 1000 }
  );

  const tags = useMemo(() => {
    if (!tagIds?.length || !catalog) return [];
    const byId = new Map(catalog.map((tag) => [tag.id, tag]));
    return tagIds.flatMap((id) => {
      const tag = byId.get(id);
      return tag ? [tag] : [];
    });
  }, [tagIds, catalog]);

  if (tags.length === 0) return null;

  const visible = tags.slice(0, max);
  const hidden = tags.slice(max);
  const hiddenNames = hidden.map((tag) => tag.name).join(", ");

  return (
    <div className={cn("flex min-w-0 flex-wrap items-center gap-1", className)}>
      {visible.map((tag) => (
        <TagChip
          key={tag.id}
          name={tag.name}
          color={tag.color}
          archived={tag.status === "archived"}
        />
      ))}
      {hidden.length > 0 ? (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                aria-label={`${hidden.length} etiquetas más: ${hiddenNames}`}
                className="shrink-0 cursor-default rounded-pill bg-neutral-100 px-2 py-0.5 text-xs font-medium text-neutral-600"
              >
                +{hidden.length}
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-[240px]">
              <div className="flex flex-wrap gap-1">
                {hidden.map((tag) => (
                  <TagChip
                    key={tag.id}
                    name={tag.name}
                    color={tag.color}
                    archived={tag.status === "archived"}
                  />
                ))}
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : null}
    </div>
  );
};

export default LeadTagChips;
